import React, { useState, useMemo, useEffect } from 'react';
import { Container, Button, Grid, Input, Icon, Dropdown, Form, Step, Segment, Table, Message, DropdownItemProps, Checkbox, Loader, Divider } from 'semantic-ui-react';
import Globals from "../Globals";
import { v4 as uuidv4 } from 'uuid';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { NavLink } from 'react-router-dom';
import StripeApi from '../api/StripeApi';
import IVisitSlot from '../Models/IVisitSlot';
import OpeningHoursApi from '../api/OpeningHoursApi';

const ReservationAnniversaire: React.FC = () => {
  const currentAction = document.location.hash && document.location.hash.indexOf('?action=') > 0 ? document.location.hash.split('?action=').pop() : null;
  let initStep = 1;
  let bookingId = undefined;
  if (currentAction) {
    if (currentAction.indexOf("payment_success") === 0) {
      initStep = 4;
      let idxBookingId = document.location.hash.indexOf('&id=');
      if (idxBookingId > 0) bookingId = document.location.hash.substr(idxBookingId + 4);
    }
    else if (currentAction.indexOf("cancel") === 0) {
      initStep = 5;
    }
  }

  const [step, setStep] = useState<number>(initStep);
  const [selectedMonth, setSelectedMonth] = useState<string>();
  const [selectedDate, setSelectedDate] = useState<string>();
  const [selectedSlotId, setSelectedSlotId] = useState<string>();
  const [zipCode, setZipCode] = useState<string>(document.location.hostname === 'localhost' ? '93160' : '');
  const [firstName, setFirstName] = useState<string>(document.location.hostname === 'localhost' ? 'Gaetan' : '');
  const [lastName, setLastName] = useState<string>(document.location.hostname === 'localhost' ? 'Test' : '');
  const [email, setEmail] = useState<string>(document.location.hostname === 'localhost' ? 'test@fermerainbow.com' : '');
  const emailIsValid = useMemo<boolean>(() => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }, [email]);
  const [verificationEmail, setVerificationEmail] = useState<string>(document.location.hostname === 'localhost' ? 'test@fermerainbow.com' : '');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [stripe, setStripe] = useState<Stripe | null>();
  const [step2IsValid, setStep2IsValid] = useState<boolean>(false);
  const [proceedingToPayment, setProceedingToPayment] = useState<boolean>(false);
  const [displayEmailError, setDisplayEmailError] = useState<boolean>(false);
  const [displayVerificationEmailError, setDisplayVerificationEmailError] = useState<boolean>(false);
  const [displayZipCodeError, setDisplayZipCodeError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [visitMonthOptions, setVisitMonthOptions] = useState<DropdownItemProps[]>([]);
  const [visitDayOptions, setVisitDaysOptions] = useState<DropdownItemProps[]>([]);
  const [visitSlots, setVisitSlots] = useState<IVisitSlot[]>();
  const [validatedCGV, setValidatedCGV] = useState<boolean>(false);

  const executeScroll = () => {
    const elt = document.getElementById('reservation-anniversaire');
    if (elt) elt.scrollIntoView();
  }

  const stepTitle = useMemo(() => {
    switch (step) {
      case 1:
        return "Date de l'anniversaire et coordonnées";
      case 2:
        return "Récapitulatif de la commande";
      case 3:
        return "Commande validée";
      case 4:
        return "Commande annulée";
    }
  }, [step]);

  useEffect(() => {
    if (!stripe) {
      const stripeApi = new StripeApi();
      stripeApi.getPublicKey().then((res) => {
        loadStripe(res.data)
          .then((res) => {
            setStripe(res);
          });
      });
    }
  }, [stripe]);

  useEffect(() => {
    const daysOfWeek = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const monthsOfYear = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    if (!visitSlots) {
      const opHoursApi = new OpeningHoursApi();
      opHoursApi.getBirthdayBookingOpeningHours()
        .then((res) => {
          const visDays = res.data.map(r => {
            const day = new Date(r.date);

            const year = day.getFullYear().toString();
            const month = day.getMonth() + 1;
            const paddedMonth = (month < 10) ? `0${month}` : month.toString();
            const curday = day.getDate();
            const paddedDay = (curday < 10) ? `0${curday}` : curday.toString();

            // Disable for 2 months
            if (year === "2022" && (month === 9 || (month === 10 && curday < 29))) r.available = false;

            return {
              text: `${daysOfWeek[day.getDay()]} ${paddedDay} ${monthsOfYear[month - 1]} ${year} ${r.startHour}-${r.endHour}h`,
              value: `${year}/${paddedMonth}/${paddedDay} ${r.startHour}:00`,
              key: r.id, // r.date + '-' + r.startHour,
              disabled: r.available === false,
              icon: r.available === false ? 'delete calendar' : 'birthday'
            };
          });
          const visMonth = res.data.map(r => r.date.substr(0, 7)).filter((val, idx, arr) => arr.indexOf(val) === idx).map(r => {
            const day = new Date(r + '-01');

            const year = day.getFullYear().toString();
            const month = day.getMonth() + 1;
            const paddedMonth = (month < 10) ? `0${month}` : month.toString();

            return {
              text: `${monthsOfYear[month - 1]} ${year}`,
              value: `${year}/${paddedMonth}`,
              key: r,
            };
          });
          setVisitMonthOptions(visMonth);
          setVisitDaysOptions(visDays);
          setVisitSlots(res.data);
        });
    }
  }, [visitSlots]);

  useEffect(() => {
    if (selectedDate && lastName && firstName && zipCode && zipCode.trim().length === 5 && emailIsValid && verificationEmail === email) {
      return setStep2IsValid(true);
    }
    else {
      return setStep2IsValid(false);
    }
  }, [selectedDate, lastName, firstName, zipCode, email, verificationEmail, emailIsValid]);

  const proceedToPayment = async (ev: any) => {
    // Block native form submission.
    ev.preventDefault();

    setProceedingToPayment(true);
    if (!stripe) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      return;
    }

    // Create a new Checkout Session using the server-side endpoint
    const stripeApi = new StripeApi();
    stripeApi.createBirthdaySession({
      Id: uuidv4(),
      Lastname: lastName,
      FirstName: firstName,
      Email: email,
      Phone: phoneNumber,
      ZipCode: zipCode,
      EventDate: new Date(`${selectedDate}:00`),
      Children: 0,
      Parents: 0,
      Option1: 0,
      Option2: 0,
      Option3: 0,
      Option4: 0,
      Option5: 0,
      ItemId: selectedSlotId || ''
    })
      .then((session) => {
        return stripe.redirectToCheckout({ sessionId: session.data.id });
      })
      .then(function (result) {
        // If `redirectToCheckout` fails due to a browser or network
        // error, you should display the localized error message to your
        // customer using `error.message`.
        if (result.error) {
          setErrorMessage(result.error.message);
          setProceedingToPayment(false);
        }
      })
      .catch(function (error) {
        setErrorMessage(error.message)
        console.error('Error:', error.message);
        setProceedingToPayment(false);
      });
  };

  return (
    <Container fluid>
      {step <= 2 && (
        <Step.Group widths="three" size="tiny">
          <Step active={step === 1} icon="ticket" title="Date et coordonnées" completed={step > 1} />
          <Step active={step === 2} completed={step === 4} icon="payment" title="Récapitulatif et paiement" />
        </Step.Group>
      )}
      <h4>{stepTitle}</h4>
      {!visitSlots && (
        <Segment textAlign='center'>
          <Loader active inline size='small' />
          Chargement du planning des anniversaires...
        </Segment>
      )}
      {visitSlots && step === 1 && (
        <React.Fragment>
          <Grid stackable>
            <Grid.Row columns="2">
              <Grid.Column><label>Sélectionnez le mois</label>
                <Dropdown
                  placeholder='Sélectionnez le mois'
                  fluid
                  selection
                  key="visitMonthChoice"
                  value={selectedMonth}
                  onChange={(e, data) => { setSelectedMonth(data.value as string); setSelectedDate(undefined); }}
                  options={visitMonthOptions}
                />
              </Grid.Column>
              <Grid.Column>
                <label>Sélectionnez la date et le créneau horaire *</label>
                <Dropdown
                  placeholder="Sélectionnez la date et l'heure"
                  fluid
                  selection
                  key="visitDateChoice"
                  value={selectedDate}
                  onChange={(e, data) => {
                    setSelectedDate(data.value as string);
                    const selDay = visitDayOptions.find(d => d.value === data.value as string);
                    if (selDay)
                      setSelectedSlotId(selDay.key);
                    else
                      setSelectedSlotId(undefined);
                  }}
                  options={visitDayOptions.filter(d => {
                    return (d.value as string).startsWith(selectedMonth as string)
                  }
                  )}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <Divider horizontal>Coordonnées</Divider>
          <Form>
            <Form.Group widths="equal">
              <Form.Field required>
                <label>Nom</label>
                <Input value={lastName} key="lastName" onChange={(ev, { value }) => { setLastName(value); }} />
              </Form.Field>
              <Form.Field required>
                <label>Prénom</label>
                <Input value={firstName} key="firstName" onChange={(ev, { value }) => { setFirstName(value); }} />
              </Form.Field>
              <Form.Field required>
                <label>Code Postal</label>
                <Input value={zipCode} key="zipCode" maxLength="5"
                  onChange={(ev, { value }) => {
                    setZipCode(value);
                  }}
                  onBlur={() => {
                    setDisplayZipCodeError((!zipCode || zipCode.length < 5));
                  }}
                />
                {displayZipCodeError && (
                  <Message size="mini" negative={true}>Le code postal n'est pas valide (doit être composé de 5 caractères numériques).</Message>
                )}
              </Form.Field>
            </Form.Group>
            <Form.Group widths="equal">
              <Form.Field required>
                <label>Adresse email</label>
                <Input value={email} key="email"
                  onChange={(ev, { value }) => { setEmail(value); }}
                  onBlur={() => {
                    setDisplayEmailError(!emailIsValid);
                  }} />
                {displayEmailError && (
                  <Message size="mini" negative={true}>Email invalide</Message>
                )}
              </Form.Field>
              <Form.Field required>
                <label>Vérification adresse email</label>
                <Input value={verificationEmail} key="emailVerification"
                  onChange={(ev, { value }) => { setVerificationEmail(value); }}
                  onBlur={() => {
                    setDisplayVerificationEmailError(email !== verificationEmail);
                  }} />
                {displayVerificationEmailError && (
                  <Message size="mini" negative={true}>L'email de confirmation doit être le même que l'email.</Message>
                )}
              </Form.Field>
              <Form.Field>
                <label>Téléphone (facultatif)</label>
                <Input value={phoneNumber} key="phoneNumber" onChange={(ev, { value }) => { setPhoneNumber(value); }} />
              </Form.Field>
            </Form.Group>
            <p>Veuillez bien vérifier votre adresse email, celle-ci est importante car votre attestation de réservation et votre reçu de paiement y seront envoyés.</p>
            <Grid>
              <Grid.Row columns="2">
                <Grid.Column>
                </Grid.Column>
                <Grid.Column textAlign="right">
                  <Button size='medium' color='green' icon labelPosition='right' onClick={() => { setStep(2); executeScroll(); }} disabled={step2IsValid === false}><Icon name='payment' /> Etape suivante</Button>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Form>
        </React.Fragment>
      )}
      {step === 2 && (
        <React.Fragment>
          <Table celled striped unstackable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell colSpan='3'>Votre réservation pour un anniversaire à la ferme le <b>{visitDayOptions && visitDayOptions.filter(d => d.value === selectedDate)[0].text}</b></Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              <Table.Row>
                <Table.Cell><b>Total</b></Table.Cell>
                <Table.Cell></Table.Cell>
                <Table.Cell collapsing textAlign='right'><b>{Globals.TarifAnniversaire} &euro;</b></Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>
          <Segment>
            <Checkbox
              label='En cochant cette case, je reconnais avoir pris connaissance et accepté les conditions générales de vente : '
              onChange={() => setValidatedCGV(!validatedCGV)}
              checked={validatedCGV}
            />
            &nbsp;<b><a href="/wp-content/app/docs/Conditions générales de vente -Prestations anniversaire à la ferme.pdf" target="_blank" rel="noopener noreferrer" title="Consulter les conditions de vente">consulter les CGV</a></b>.<br />
            En cliquant sur "Procéder au paiement", vous serez redirigé vers le site de paiement sécurisé en ligne afin de finaliser votre achat.
          </Segment>
          <Grid>
            <Grid.Row columns="2">
              <Grid.Column>
                <Button size='small' icon labelPosition='left' onClick={() => { setStep(2); executeScroll(); }} disabled={proceedingToPayment}><Icon name='arrow left' /> Etape précédente</Button>
              </Grid.Column>
              <Grid.Column textAlign="right">
                <Button size="medium" color='green' icon labelPosition='right' loading={proceedingToPayment} disabled={(!validatedCGV || proceedingToPayment)} onClick={proceedToPayment}><Icon name='credit card' /> Procéder au paiement</Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
          {proceedingToPayment && (
            <Segment textAlign='center'>
              <Loader active inline size='small' />
              Veuillez patienter, vous allez être redirigé sur le site de paiement Stripe...
            </Segment>)
          }
        </React.Fragment>
      )}
      {step === 3 && (
        <React.Fragment>
          <p>Votre numéro de réservation est <b>{bookingId}</b>.<br />Vous allez recevoir un email récapitulatif avec l'attestation de réservation de la part de <b>reservations@fermerainbow.com</b> à présenter à l'accueil lors de votre visite, ainsi qu'un deuxième avec votre reçu de paiement de la part de <b>support@stripe.com</b>. Vérifiez-bien que votre boite de messagerie ne les a pas bloqués.</p>
          <p style={{ textAlign: "center" }}><img src={`/api/qrcode/${bookingId}`} alt="" width="300" height="300" /></p>
          <p className="noprint">Vous pouvez sauvegarder cette page, celle-ci pourra servir d'attestation en cas d'email non reçu.</p>
          <p className="noprint"><Button onClick={() => { document.location.assign('/') }} color="green">Retourner à l'accueil du site</Button></p>
        </React.Fragment>
      )}
      {step === 4 && (
        <React.Fragment>
          <p>Vous avez annulé la commande ou un problème est survenu.</p>
          <p><Button as={NavLink} to="/" color="green" exact>Retourner à l'accueil du site</Button> <Button as={NavLink} to="/billetterie" color="green" exact>Retenter la réservation</Button></p>
        </React.Fragment>
      )}
      <Message negative={true} hidden={!errorMessage}>{errorMessage}</Message>
    </Container>
  );
}

export default ReservationAnniversaire;
